<script setup>
// GPS check-in body: stylized radar map + "Check my location" CTA, plus the
// GPS failure-recovery experience (mobile-web GPS is the #1 client pain point
// — three permission layers can each fail silently).
//
// State machine: idle | locating | found | outside | denied | unavailable | timeout
//
// Modes:
//   • Demo (default, unchanged): locating always succeeds after 1200ms, then
//     the success emit fires 700ms later.
//   • forceState prop ('denied'|'unavailable'|'timeout'|'outside'): the locate
//     attempt resolves to that state after ~1200ms — powers the debug toggle
//     so the recovery UX is demoable.
//   • Real (spec.realGps === true): navigator.geolocation.getCurrentPosition
//     with { enableHighAccuracy, timeout: 12000, maximumAge: 30000 }; error
//     codes 1→denied, 2→unavailable, 3→timeout, plus an independent 15s UI
//     watchdog (iOS Safari can hang without ever firing the error callback).
//
// Port of `GpsBody` in the source `_shared/CheckInSheet.tsx`.
import { ref, computed, watch, onUnmounted } from 'vue'
import { Check, Loader2, MapPinOff, LocateOff, Clock, Compass } from '@lucide/vue'
import { resolveRecoveryCopy } from './recoveryCopy'
import { computeCheckInAvailability } from './checkin'
import BlockedNotice from './BlockedNotice.vue'
import RecoveryPanel from './RecoveryPanel.vue'

const props = defineProps({
  spec: { type: Object, required: true },
  // Debug hook: 'denied' | 'unavailable' | 'timeout' | 'outside' — the locate
  // attempt resolves to this state after ~1200ms instead of succeeding.
  forceState: { type: String, default: null },
  // Program-config override for the recovery copy (per failure state), keyed the
  // same as DEFAULT_RECOVERY_COPY. Falls back to the platform defaults when absent
  // so the flow works standalone. Backend (ENG-2205) supplies this.
  recoveryCopy: { type: Object, default: null },
  // Live validation: async ({ latitude, longitude }) => { ok, message }. The
  // inside/outside verdict is ALWAYS the server's — the local distance check
  // remains only for the mock demo. Permission failures (denied/unavailable/
  // timeout) never reach the server; they stay client-side recovery states.
  submit: { type: Function, default: null },
})

// `success` carries the coords that passed, so combo parents (gps_and_*) can
// include them in the final combined submission.
const emit = defineEmits(['success', 'use-pin', 'help'])

const GPS_FAILURE_STATES = ['denied', 'unavailable', 'timeout', 'outside']

const state = ref('idle')
const serverError = ref('')
// True once locating has run > 4s — shows the indoor-GPS reassurance line.
const slowLocating = ref(false)
const proximity = computed(() => props.spec.simulatedProximity ?? 0.85) // demo
const availability = computed(() => computeCheckInAvailability(props.spec.details))
const blocked = computed(() => !availability.value.ok)
const failed = computed(() => GPS_FAILURE_STATES.includes(state.value))
// "Use venue PIN instead" only when the spec actually carries a PIN
// (gps_or_pin, or a staff-override pin on a gps/gps_and_* spec).
const pinAvailable = computed(() => Boolean(props.spec.pin))

const GEO_OPTIONS = { enableHighAccuracy: true, timeout: 12000, maximumAge: 30000 }
// Independent UI watchdog — longer than the geolocation timeout so the API
// gets first say, but guarantees we never hang on iOS Safari.
const WATCHDOG_MS = 15000

let pingTimer = null
let successTimer = null
let slowTimer = null
let watchdogTimer = null
// Guards the real-mode race between callback, error callback and watchdog.
let settled = false

const clearLocateTimers = () => {
  clearTimeout(pingTimer)
  clearTimeout(slowTimer)
  clearTimeout(watchdogTimer)
  pingTimer = null
  slowTimer = null
  watchdogTimer = null
}

const settle = (next) => {
  if (settled) return
  settled = true
  clearLocateTimers()
  slowLocating.value = false
  state.value = next
}

// Distance in meters between two lat/lng points (haversine). Kept local so
// the validation set stays a self-contained port.
const distanceMeters = (lat1, lng1, lat2, lng2) => {
  const toRad = (d) => (d * Math.PI) / 180
  const R = 6371000
  const dLat = toRad(lat2 - lat1)
  const dLng = toRad(lng2 - lng1)
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2
  return 2 * R * Math.asin(Math.sqrt(a))
}

// Real fix → found/outside. Without venue coords + radius on the spec we
// can't judge distance, so any successful fix counts as inside.
const resolvePosition = (pos) => {
  const { venueLat, venueLng, radiusM } = props.spec
  if (venueLat == null || venueLng == null || !radiusM) return 'found'
  const d = distanceMeters(pos.coords.latitude, pos.coords.longitude, venueLat, venueLng)
  return d <= radiusM ? 'found' : 'outside'
}

// The coords that produced the current verdict — emitted with `success`.
let lastCoords = null

// Live path: the fix is in hand, the SERVER decides inside/outside.
const resolveWithServer = (pos) => {
  lastCoords = { latitude: pos.coords.latitude, longitude: pos.coords.longitude }
  props.submit(lastCoords)
    .then((result) => {
      serverError.value = result?.ok ? '' : (result?.message || '')
      settle(result?.ok ? 'found' : 'outside')
    })
    .catch(() => settle('outside'))
}

const ping = () => {
  settled = false
  serverError.value = ''
  slowLocating.value = false
  state.value = 'locating'
  slowTimer = setTimeout(() => {
    slowLocating.value = true
  }, 4000)

  // Debug hook: resolve to the forced failure state after ~1200ms.
  if (props.forceState && GPS_FAILURE_STATES.includes(props.forceState)) {
    pingTimer = setTimeout(() => settle(props.forceState), 1200)
    return
  }

  // Real mode.
  if (props.spec.realGps && typeof navigator !== 'undefined' && navigator.geolocation) {
    watchdogTimer = setTimeout(() => settle('timeout'), WATCHDOG_MS)
    navigator.geolocation.getCurrentPosition(
      (pos) => (props.submit ? resolveWithServer(pos) : settle(resolvePosition(pos))),
      (err) => settle(err.code === 1 ? 'denied' : err.code === 2 ? 'unavailable' : 'timeout'),
      GEO_OPTIONS,
    )
    return
  }

  // Demo mode: always succeed so reward preview is reachable.
  pingTimer = setTimeout(() => settle('found'), 1200)
}

watch(state, (v) => {
  if (v === 'found') {
    successTimer = setTimeout(() => emit('success', lastCoords), 700)
  }
})

onUnmounted(() => {
  clearLocateTimers()
  clearTimeout(successTimer)
})

// Icons stay in code (per failure state); the copy is data — the program-config
// override (props.recoveryCopy) wins over the platform defaults, with {radius}
// interpolated from the live spec. See recoveryCopy.js.
const RECOVERY_ICONS = {
  denied: MapPinOff,
  unavailable: LocateOff,
  timeout: Clock,
  outside: Compass,
}
const failureMeta = computed(() => {
  const icon = RECOVERY_ICONS[state.value]
  if (!icon) return null
  const copy = resolveRecoveryCopy(
    state.value,
    props.recoveryCopy?.[state.value],
    { radius: props.spec.radiusLabel },
  )
  return { icon, ...copy }
})

const youMarkerStyle = computed(() => ({
  left: `${50 + (1 - proximity.value) * 30}%`,
  top: `${50 - (1 - proximity.value) * 18}%`,
  transform: 'translate(-50%, -50%)',
}))
</script>

<template>
  <!-- Failure-recovery view: replaces the radar area entirely. -->
  <RecoveryPanel
    v-if="failed && failureMeta"
    :icon="failureMeta.icon"
    :headline="failureMeta.headline"
    :body="failureMeta.body"
    :steps="failureMeta.steps"
    :pin-available="pinAvailable"
    id-prefix="checkin-gps-recovery"
    @retry="ping"
    @use-pin="$emit('use-pin')"
    @help="$emit('help')"
  />

  <div v-else class="cs-gps">
    <!-- Stylized radar map -->
    <div class="cs-gps__radar">
      <div class="cs-gps__radar-bg" />
      <div class="cs-gps__radar-ring cs-gps__radar-ring--outer" />
      <div class="cs-gps__radar-ring cs-gps__radar-ring--inner" />
      <!-- Venue marker -->
      <div class="cs-gps__venue-marker" />
      <!-- You marker -->
      <div class="cs-gps__you-marker" :style="youMarkerStyle" />
      <div v-if="state === 'locating'" class="cs-gps__ping-wrap">
        <div class="cs-gps__ping" />
      </div>
    </div>

    <div class="cs-gps__status">
      <span v-if="state === 'found'" class="cs-gps__status-line cs-gps__status-line--found">
        <Check :size="14" /> Inside the radius — checking you in
      </span>
      <template v-else>{{ serverError || `You need to be ${spec.radiusLabel}.` }}</template>
      <div v-if="state === 'locating' && slowLocating" class="cs-gps__reassure">
        Still looking — GPS can take a moment indoors. Standing near a window helps.
      </div>
    </div>

    <div class="cs-gps__footer">
      <BlockedNotice :availability="availability" />
      <button
        id="checkin-gps-locate"
        type="button"
        class="cs-gps__cta gtm-click"
        :disabled="blocked || state === 'locating' || state === 'found'"
        @click="ping"
      >
        <template v-if="blocked">
          {{ availability.nextAvailableLabel
            ? `Next check-in ${availability.nextAvailableLabel}`
            : availability.blockedLabel ?? 'Unavailable' }}
        </template>
        <span v-else-if="state === 'locating'" class="cs-gps__cta-inner">
          <Loader2 class="cs-gps__spinner" :size="14" /> Locating…
        </span>
        <template v-else-if="state === 'found'">Checked in!</template>
        <template v-else>Check my location</template>
      </button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use './checkin-shared' as *;

// Source: `space-y-3 pt-1`
.cs-gps {
  padding-top: 4px; // pt-1

  > * + * {
    margin-top: 12px; // space-y-3
  }

  // Source: `relative mx-auto grid h-36 w-36 place-items-center`
  &__radar {
    position: relative;
    margin-left: auto;
    margin-right: auto;
    display: grid;
    height: 144px; // h-36
    width: 144px; // w-36
    place-items: center;
  }

  // Source: `absolute inset-0 rounded-full bg-current/5`
  &__radar-bg {
    position: absolute;
    inset: 0;
    border-radius: 9999px;
    background: cs-current(5%);
  }

  // Source: `absolute inset-4 / inset-10 rounded-full border border-current/15`
  &__radar-ring {
    position: absolute;
    border-radius: 9999px;
    border: 1px solid cs-current(15%);

    &--outer {
      inset: 16px; // inset-4
    }

    &--inner {
      inset: 40px; // inset-10
    }
  }

  // Source: `absolute left-1/2 top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2
  //          rounded-full ${theme.primaryBtn}`
  &__venue-marker {
    position: absolute;
    left: 50%;
    top: 50%;
    height: 12px; // h-3
    width: 12px; // w-3
    transform: translate(-50%, -50%);
    border-radius: 9999px;
    background: var(--fr-primary, #FF9900);
  }

  // Source: `absolute h-2.5 w-2.5 rounded-full bg-emerald-500 ring-4 ring-emerald-500/25`
  &__you-marker {
    position: absolute;
    height: 10px; // h-2.5
    width: 10px; // w-2.5
    border-radius: 9999px;
    background: $cs-emerald-500;
    box-shadow: 0 0 0 4px rgba($cs-emerald-500, 0.25); // ring-4 ring-emerald-500/25
  }

  // Source: `absolute inset-0 grid place-items-center` wrapping the ping circle
  &__ping-wrap {
    position: absolute;
    inset: 0;
    display: grid;
    place-items: center;
  }

  // Source: `h-full w-full animate-ping rounded-full bg-current/10`
  &__ping {
    height: 100%;
    width: 100%;
    border-radius: 9999px;
    background: cs-current(10%);
    animation: cs-ping-kf 1s cubic-bezier(0, 0, 0.2, 1) infinite;
  }

  // Source: `text-center text-[12px] ${theme.muted}`
  &__status {
    text-align: center;
    font-size: 12px;
    color: var(--fr-muted, #6b7280);
  }

  // Reassurance line after 4s of locating.
  &__reassure {
    margin-top: 8px; // space-y-2 rhythm
    font-size: 11px;
    color: var(--fr-muted, #6b7280);
  }

  // Source: `flex items-center justify-center gap-1 text-emerald-600`
  &__status-line {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px; // gap-1

    &--found {
      color: $cs-emerald-600;
    }
  }

  &__footer {
    @include cs-sticky-footer;
  }

  &__cta {
    @include cs-cta;
  }

  &__cta-inner {
    @include cs-cta-inner;
  }

  &__spinner {
    @include cs-spin;
  }
}

// Tailwind `animate-ping`
@keyframes cs-ping-kf {
  75%,
  100% {
    transform: scale(2);
    opacity: 0;
  }
}
</style>
